import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { logEvent, Analytics } from 'firebase/analytics';
import { analytics as analyticsPromise, db as firestoreDb } from '../lib/firebase';
import { collection, addDoc, doc, getDoc } from 'firebase/firestore';
import ReactGA from 'react-ga4';

interface AnalyticsContextType {
  trackEvent: (eventName: string, params?: Record<string, unknown>) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [isGAInitialized, setIsGAInitialized] = useState(false);

  useEffect(() => {
    analyticsPromise.then(setAnalytics);

    // Initialize GA4 from DB config
    const initGA = async () => {
      try {
        const configDoc = await getDoc(doc(firestoreDb, 'config', 'store'));
        if (configDoc.exists()) {
          const config = configDoc.data();
          const gaId = config.integrations?.googleAnalytics;
          if (gaId && gaId.startsWith('G-')) {
            ReactGA.initialize(gaId);
            setIsGAInitialized(true);
            console.warn('GA4 Initialized with ID:', gaId);
          }
        }
      } catch (error) {
        console.error('Error initializing GA4:', error);
      }
    };

    initGA();
  }, []);

  const trackEvent = useCallback(
    async (eventName: string, params?: Record<string, unknown>) => {
      // 1. GA4 (via react-ga4)
      if (isGAInitialized) {
        ReactGA.event({ category: 'User', action: eventName, ...params });
      }

      // 2. Firebase Analytics (GA4 via SDK)
      if (analytics) {
        logEvent(analytics, eventName, params);
      }

      // 3. Log to Firestore 'events' collection for custom dashboard
      try {
        const safeParams = params ? JSON.parse(JSON.stringify(params)) : {};

        await addDoc(collection(firestoreDb, 'events'), {
          eventName,
          params: safeParams,
          path: location.pathname,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
        });
      } catch (error) {
        console.error('Error logging event to Firestore:', error);
      }
    },
    [analytics, isGAInitialized, location.pathname]
  );

  // Track Page Views when location changes or GA/Analytics is ready
  useEffect(() => {
    if (isGAInitialized) {
      ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
    }

    if (analytics) {
      logEvent(analytics, 'page_view', {
        page_path: location.pathname + location.search,
      });
    }

    // Also track as a generic event for our dashboard
    trackEvent('page_view', {
      path: location.pathname + location.search,
    });

    // --- Session Based Visitor Tracking ---
    const SESSION_KEY = 'alphamed_last_visit';
    const SESSION_DURATION = 60 * 60 * 1000; // 1 hour

    const checkSession = async () => {
      const now = Date.now();
      const lastVisit = localStorage.getItem(SESSION_KEY);

      // If no last visit or last visit was > 1 hour ago
      if (!lastVisit || now - parseInt(lastVisit) > SESSION_DURATION) {
        // New Session
        try {
          // Dynamically import to avoid circular dependencies
          const { db: serviceDb } = await import('../services/backend');
          await serviceDb.trackVisit();
          console.warn('New session recorded:', new Date().toISOString());
        } catch (err) {
          console.error('Failed to record visit:', err);
        }
      }

      // Always update the timestamp to extend the session
      localStorage.setItem(SESSION_KEY, now.toString());
    };

    checkSession();
  }, [location, isGAInitialized, analytics, trackEvent]);

  return <AnalyticsContext.Provider value={{ trackEvent }}>{children}</AnalyticsContext.Provider>;
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
